import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { createPromptEditor } from '@atlassian/editor-ai-injected-editors/prompt-editor';
import {
	createPageEditorPluginAI,
	createPageEditorPluginAIProvider,
	createPageCommentEditorPluginAIProvider,
} from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import { useBooleanFeatureFlag, useSessionData } from '@confluence/session-data';

import { useLinkingFeatures } from '../components/EditorLinkingFeatures';

import type { AIGlobalOptIn } from './useEditorAIOptIn/useEditorAIOptIn';

export type Sentiment = 'good' | 'bad' | 'unknown';

type EditorPluginProvider = Parameters<typeof createPageEditorPluginAIProvider>[0];
type HandleFeedbackSubmission = NonNullable<EditorPluginProvider>['handleFeedbackSubmission'];

export type HandleFeedbackSubmissionArgs = Parameters<NonNullable<HandleFeedbackSubmission>>[0];

interface EditorPluginAIOptions {
	appearance?: 'full-page' | 'page-comment';
	/**
	 * Triggers when "thumbs up/down" feedback buttons are is clicked - probably
	 * want to start a survey (freeform field) at this point to collect any
	 * further details.
	 *
	 * Sentiment shape/details may change in future, we may expose that information
	 * more directly as explicit data shapes, but abstracting away to events
	 * for now to get an early iteration working.
	 */

	onFeedbackClicked?: (props: HandleFeedbackSubmissionArgs) => void;
	/**
	 * Triggers when the AI plugin enters a review state (response finished streaming)
	 */
	onReviewStateEntered?: () => void;
	/**
	 * Triggers when the AI plugin experience is complete, e.g. user has inserted
	 * content into the page, ending the AI experience.
	 */
	onExperienceComplete?: () => void;
	/**
	 * Opt in status of AI
	 */
	optInStatus: AIGlobalOptIn['optInStatus'];
	/**
	 * Triggered when opt in status of AI is disabled-opt-in
	 */
	triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	AIButtonWrapper?: React.FC<{ children: React.ReactNode }>;
	proactiveAIToolbarButtonWrapper?: React.FC<{ children: React.ReactNode }>;

	/**
	 * Used to generate hash for pages channelID with use of Rovo agents.
	 */
	getChannelVariables?: EditorPluginProvider['getChannelVariables'];

	/**
	 * Triggered when the document has been changed by a Rovo agent.
	 */
	onDocChangeByAgent?: EditorPluginProvider['onDocChangeByAgent'];
}

const CARD_OPTIONS = { allowDatasource: true };

const defaultOptions: EditorPluginAIOptions = {
	appearance: 'full-page',
	onFeedbackClicked: () => {},
	onReviewStateEntered: () => {},
	onExperienceComplete: () => {},
	triggerOptInFlow: () => {},
	optInStatus: 'disabled',
};

export const EDITOR_PLUGIN_AI_FEATURE_FLAG = 'confluence.frontend.fabric.editor.editor-plugin-ai';

export const useIsEditorPluginAIEnabled = (): boolean => {
	const { isAdminHubAIEnabled } = useSessionData();
	const isAIFeatureFlagEnabled = useBooleanFeatureFlag(EDITOR_PLUGIN_AI_FEATURE_FLAG);
	return isAIFeatureFlagEnabled && isAdminHubAIEnabled;
};

export const useEditorPluginAIProvider = (
	options?: EditorPluginAIOptions,
): ReturnType<typeof createPageEditorPluginAIProvider> => {
	const optionsToUse = options || defaultOptions;

	const provider = useMemo(
		() =>
			optionsToUse.appearance === 'page-comment'
				? createPageCommentEditorPluginAIProvider
				: createPageEditorPluginAIProvider,
		[optionsToUse.appearance],
	);

	const optionsToUseRef = useRef(optionsToUse);
	optionsToUseRef.current = optionsToUse;

	const linkingProp = useLinkingFeatures(CARD_OPTIONS);
	const PromptEditor = useMemo(
		() =>
			createPromptEditor({
				linking: linkingProp.linking,
				featureFlags: {},
			}),
		[linkingProp.linking],
	);

	/**
	 * Supported locales are currently controlled from the
	 * confluence.frontend.editor.ai-proactive-ai-config feature flag
	 * which will be a comment separated string of locales, e.g.
	 * "en-US,en-GB"
	 */
	const { locale } = useIntl();

	return useMemo(
		() =>
			provider({
				generativeAIApiUrl: `${window.location.origin}/gateway/api/editor-ai/v1/generative/ai`,
				proactiveAIConfig: {
					enabled: ['en-US', 'en-GB'].includes(locale),
				},
				handleFeedbackSubmission: async ({ sentiment, getAIExperience, editorAttributes }) => {
					try {
						// TODO: EDF-831: Fix and ensure types match with handleFeedbackSubmission
						// @ts-ignore
						optionsToUseRef.current.onFeedbackClicked?.({
							sentiment,
							getAIExperience,
							editorAttributes,
						});
						return { status: 'submitted' as const };
					} catch (err) {
						return { status: 'failed' as const };
					}
				},
				onExperienceEvent: {
					// Thumbs up and down are being deprecated and are no longer used.
					THUMBS_UP: () => {},
					THUMBS_DOWN: () => {},
					REVIEW_STATE_ENTERED: () => {
						optionsToUseRef.current.onReviewStateEntered?.();
					},
					EXPERIENCE_COMPLETE: () => {
						optionsToUseRef.current.onExperienceComplete?.();
					},
				},
				onDocChangeByAgent: (_agent) => {
					// TODO Integrate with confluence
				},
				getChannelVariables: optionsToUseRef.current.getChannelVariables,
				PromptEditor,
				AIButtonWrapper: options?.AIButtonWrapper,
				proactiveAIToolbarButtonWrapper: options?.proactiveAIToolbarButtonWrapper,
			}),
		[
			provider,
			locale,
			PromptEditor,
			options?.AIButtonWrapper,
			options?.proactiveAIToolbarButtonWrapper,
		],
	);
};

export const getEditorPluginAI = (options?: EditorPluginAIOptions): any[] => {
	const optionsToUse = options || defaultOptions;

	return [
		createPageEditorPluginAI({
			editorPluginAIProvider: createPageEditorPluginAIProvider({
				generativeAIApiUrl: `${window.location.origin}/gateway/api/editor-ai/v1/generative/ai`,
				handleFeedbackSubmission: async ({ sentiment, getAIExperience, editorAttributes }) => {
					try {
						// TODO: EDF-831: Fix and ensure types match with handleFeedbackSubmission
						// @ts-ignore
						optionsToUse.onFeedbackClicked?.({
							sentiment,
							getAIExperience,
							editorAttributes,
						});
						return { status: 'submitted' as const };
					} catch (err) {
						return { status: 'failed' as const };
					}
				},
				onExperienceEvent: {
					// Thumbs up and down are being deprecated and are no longer used.
					THUMBS_UP: () => {},
					THUMBS_DOWN: () => {},
					REVIEW_STATE_ENTERED: () => {
						optionsToUse.onReviewStateEntered?.();
					},
					EXPERIENCE_COMPLETE: () => {
						optionsToUse.onExperienceComplete?.();
					},
				},
			}),
			aiGlobalOptIn: {
				status: optionsToUse.optInStatus,
				triggerOptInFlow: optionsToUse.triggerOptInFlow,
			},
		}),
	];
};
